// ** UI
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ** Utils
import { normalizeDateLabel } from '@/utils/shared/stringUtils';

// ** Components
import Link from '@/components/common/Link';
import UnderlineHoverCta from '@/components/common/UnderlineHoverCta';
import { Icon } from '@/components/common/icons';

export type EventCardMode = 'online' | 'praesenz';

export type EventCardModel = {
	id: string;
	dateLabel: string;
	mode: EventCardMode;
	locationLabel?: string;
	imageUrl?: string;
	subline: string;
	copy: string;
	ctaLabel?: string;
	href?: string;
	availabilityLabel?: string;
	isSoldOut?: boolean;
	isFewPlacesLeft?: boolean;
	field: string;
};

export type EventCardProps = {
	event: EventCardModel;
};

const handleEventImage = (field: string) => {
	// List of valid fields
	const validFields = [
		'Berufsbildung',
		'Bestatter',
		'Betriebstechnik',
		'Fachtagung',
		'Formgebung',
		'Gesundheit',
		'Mitarbeiter',
		'Neue Technologien',
		'Recht',
		'Sachverständige',
		'SIAM',
		'Treffpunkt',
		'Unternehmensführung',
		'Webinar'
	];

	return validFields.includes(field) ? `/images/cards/events/${field}.jpg` : `/images/cards/events/Webinar.jpg`;
};

// const PLACEHOLDER_IMAGES = [
// 	'/images/drafts/events/event-audience.png',
// 	'/images/drafts/events/event-workshop.png',
// 	'/images/drafts/events/event-woodturning.png'
// ] as const;

const EventCard = ({ event }: EventCardProps) => {
	const theme = useTheme();

	const dateLabel = normalizeDateLabel(event.dateLabel);
	const tagLabel = event.mode === 'online' ? 'Online' : 'Präsenz';
	const tagIconColor = theme.palette.text.secondary;
	const imageUrl = handleEventImage(event.field);

	const availabilityLabel = event.availabilityLabel ?? (event.isSoldOut ? 'ausgebucht' : 'freie Plätze');

	return (
		<Card
			className="EventCard-root"
			sx={{
				position: 'relative',
				width: '100%',
				maxWidth: { xs: 361, md: 307 },
				height: { xs: 444, md: 428 },
				borderRadius: '8px',
				backgroundColor: theme.palette.common.white,
				overflow: 'hidden',
				boxShadow: theme.shadows[1],
				display: 'flex',
				flexDirection: 'column',
				minWidth: 0,

				// Micro-interactions (desktop-first): clean elevation only.
				transition: 'box-shadow 220ms ease, transform 220ms ease',
				transform: 'translateY(0px)',
				'@media (hover: hover) and (pointer: fine)': {
					'&:hover': {
						transform: 'translateY(-2px)',
						boxShadow: theme.shadows[4]
					}
				},
				'&:focus-within': {
					transform: 'translateY(-2px)',
					boxShadow: theme.shadows[3]
				},
				'@media (prefers-reduced-motion: reduce)': {
					transition: 'none',
					'&:hover': { transform: 'none' },
					'&:focus-within': { transform: 'none' },
					'& .EventCard-image': { transition: 'none' }
				}
			}}
		>
			<Link
				href={event.href ?? '#'}
				aria-label={`${event.subline} – ${event.ctaLabel ?? 'Anmelden'}`}
				className="EventCard-stretchedLink"
				display="block"
				style={{
					position: 'absolute',
					inset: 0,
					zIndex: 1,
					borderRadius: 'inherit'
				}}
			/>

			{/* Image (full-bleed) with TAG overlay */}
			<Box sx={{ position: 'relative', flexShrink: 0 }}>
				<CardMedia
					component="img"
					image={imageUrl}
					alt={event.subline || 'Event'}
					sx={{
						'&.EventCard-image': {},
						width: '100%',
						height: 180,
						objectFit: 'cover',
						transition: 'filter 220ms ease'
					}}
					className="EventCard-image"
				/>

				<Box
					sx={{
						display: 'inline-flex',
						flexDirection: 'row',
						position: 'absolute',
						top: 16,
						right: 16,
						alignItems: 'center',
						gap: 2, // 8px
						px: { xs: 3, md: 4 }, // ~12px mobile, ~16px desktop (theme spacing)
						py: { xs: 1.5, md: 2 }, // ~6px mobile, ~8px desktop (theme spacing)
						borderRadius: 999,
						backgroundColor: theme.palette.common.white,
						boxShadow: theme.shadows[1],
						transform: 'translateY(0)'
					}}
					className="EventCard-tag"
				>
					{event.mode === 'online' ? (
						<Icon name="online" size={16} color={tagIconColor} />
					) : (
						<Icon name="praesenz" width={12} height={16} color={tagIconColor} />
					)}
					<Typography
						variant="caption"
						component="div"
						sx={{
							color: theme.palette.text.secondary,
							fontSize: 14,
							lineHeight: '16px',
							letterSpacing: '-0.05px',
							whiteSpace: 'nowrap'
						}}
					>
						{tagLabel}
					</Typography>
				</Box>
			</Box>

			<Box
				sx={{
					px: { xs: 4, md: 4 },
					pb: 2,
					display: 'flex',
					flexDirection: 'column',
					flex: '1 1 auto',
					minHeight: 0,
					overflow: 'hidden'
				}}
			>
				{/* Date */}
				<Typography
					variant="caption"
					component="div"
					sx={{
						mt: 4,
						color: theme.palette.text.secondary,
						fontSize: 14,
						lineHeight: '16px',
						letterSpacing: '-0.05px',
						whiteSpace: 'nowrap',
						overflow: 'hidden',
						textOverflow: 'ellipsis'
					}}
				>
					{dateLabel}
				</Typography>

				{/* Subline bold */}
				<Typography
					variant="body-sm"
					component="div"
					sx={{
						mt: 3,
						color: theme.palette.common.black,
						fontWeight: 500,
						letterSpacing: '-0.05px',
						display: '-webkit-box',
						WebkitLineClamp: 2,
						WebkitBoxOrient: 'vertical',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						overflowWrap: 'anywhere'
					}}
				>
					{event.subline}
				</Typography>

				{/* Copy text */}
				<Typography
					variant="body-sm"
					component="div"
					sx={{
						mt: { xs: 1, md: 6 },
						'@media (min-width:992px) and (max-width:1199.95px)': {
							mt: 4
						},
						color: theme.palette.text.secondary,
						letterSpacing: '-0.05px',
						display: '-webkit-box',
						WebkitLineClamp: 3,
						WebkitBoxOrient: 'vertical',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						overflowWrap: 'anywhere'
					}}
				>
					{event.copy}
				</Typography>

				{/* CTA + Availability */}
				<Box
					sx={{
						mt: 'auto',
						pt: 8, // 32px with current theme spacing
						'@media (min-width:992px) and (max-width:1199.95px)': {
							pt: 6
						},
						display: 'grid',
						gridTemplateColumns: 'minmax(0, 1fr) auto',
						alignItems: 'baseline',
						gap: 2
					}}
				>
					<Box sx={{ minWidth: 0, overflow: 'hidden' }} inert aria-hidden="true">
						<UnderlineHoverCta
							href={event.href ?? '#'}
							label={event.ctaLabel ?? 'Anmelden'}
							color={theme.palette.text.secondary}
							typographyVariant={'body-sm'}
							fontSize={{ xs: 16, md: 18 }}
							fontWeight={500}
							lineHeight={{ xs: '22px', md: '26px' }}
							letterSpacing="-0.05px"
							activeOnParentHoverSelector=".EventCard-root"
							truncateLabel
							underlineThickness="1px"
							underlineBaseOpacity={0.2}
							underlineHoverOpacity={0.75}
							arrowShiftPx={2}
							underlineOffsetBottom={3}
						/>
					</Box>

					<Typography
						variant="caption"
						component="div"
						sx={{
							color: event.isSoldOut
								? theme.palette.error.light
								: event.isFewPlacesLeft
									? theme.palette.warning.main
									: theme.palette.success.light,
							fontSize: 14,
							lineHeight: '16px',
							letterSpacing: '-0.05px',
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							maxWidth: { xs: 120, md: 132 },
							transform: 'translateX(0)',
							transition: 'transform 260ms cubic-bezier(0.22, 1, 0.36, 1)',
							'.EventCard-root:hover & , .EventCard-root:focus-within &': {
								transform: 'translateX(-3px)'
							},
							'@media (prefers-reduced-motion: reduce)': {
								transition: 'none',
								'.EventCard-root:hover & , .EventCard-root:focus-within &': {
									transform: 'none'
								}
							}
						}}
					>
						{availabilityLabel}
					</Typography>
				</Box>
			</Box>
		</Card>
	);
};

export default EventCard;
